import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import WordListLayout from '../../../../shared/reusables/WordListLayout';
import { fetchWords } from '../../../../shared/api/darija';

/**
 * GenericWordList — covers all word-category word list views (adjectives, adverbs, pronouns, prepositions).
 *
 * Props:
 *   endpoint     {string}  API endpoint, e.g. "adjectives"
 *   title        {string}  Page title
 *   accentColor  {string}  Primary colour for darija column
 *   accentColor2 {string}  Darker variant used in WordListLayout header
 *   hoverColor   {string}  CSS rgba string for row hover background
 *   onBack       {fn}
 */
export default function GenericWordList({
    endpoint,
    title,
    accentColor,
    accentColor2,
    hoverColor,
    onBack,
}) {
    const [data, setData] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchWords(endpoint)
            .then((words) =>
                setData([...words].sort((a, b) => a.dutch.localeCompare(b.dutch, 'nl')))
            )
            .catch((err) => {
                console.error(`[GenericWordList] ${endpoint}:`, err);
                setError(err.message || 'Ophalen mislukt.');
            });
    }, [endpoint]);

    return (
        <WordListLayout
            title={title}
            subtitle={
                error
                    ? 'Fout bij laden'
                    : data.length
                    ? `${data.length} woorden · alfabetisch`
                    : 'Laden…'
            }
            accentColor={accentColor}
            accentColor2={accentColor2}
            label="Woordenlijst"
            onBack={onBack}
        >
            {error && (
                <Typography sx={{ color: '#f87171', fontFamily: "'DM Sans', sans-serif", textAlign: 'center', mt: 4 }}>
                    ⚠️ {error}
                </Typography>
            )}

            {!error && data.length > 0 && (
                <Box
                    sx={{
                        background: 'rgba(255,255,255,0.03)',
                        backdropFilter: 'blur(12px)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: '16px',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: 2,
                            px: { xs: 2, sm: 3 },
                            py: 1.2,
                            borderBottom: '1px solid rgba(255,255,255,0.08)',
                            background: 'rgba(255,255,255,0.03)',
                        }}
                    >
                        {['Nederlands', 'Darija'].map((col) => (
                            <Typography
                                key={col}
                                sx={{
                                    color: '#7878a0',
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontSize: '0.72rem',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                }}
                            >
                                {col}
                            </Typography>
                        ))}
                    </Box>

                    {data.map((item, idx) => (
                        <motion.div
                            key={item.dutch}
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: Math.min(idx * 0.01, 0.35), duration: 0.22 }}
                        >
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: 2,
                                    px: { xs: 2, sm: 3 },
                                    py: 1.5,
                                    borderBottom: idx < data.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                                    '&:hover': { background: hoverColor },
                                    transition: 'background 0.15s',
                                }}
                            >
                                <Typography sx={{ color: '#e8e8f0', fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.93rem' }}>
                                    {item.dutch}
                                </Typography>
                                <Typography sx={{ color: accentColor, fontFamily: "'DM Sans', sans-serif", fontSize: '0.93rem', fontWeight: 500 }}>
                                    {item.darija}
                                </Typography>
                            </Box>
                        </motion.div>
                    ))}
                </Box>
            )}
        </WordListLayout>
    );
}
